import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Accordion, Button } from 'ypf-pas-general-ui-components';
import { MdAssignment } from 'react-icons/md';

import LoaderContext from '../../../../../context/LoaderProvider';
import PteAutorizar from '../../../../ServiceRequest/DetailServiceRequest/TabsDetail/PteAutorizar/PteAutorizar';
import BoxesWithText from '../../../../../components/MonthlyServiceRequest/BoxesWithText/BoxesWithText';
import { postActionsInspector } from '../../../../../services/certificationsMaster';
import './MonthlyPteAutorizar.scss';
import {
  postPendingAuthorizationMonthlyUbications,
  postPendingAuthorizationMonthly,
  postCalendarData
} from '../../../../../adapters/monthlyRequestService/monthlyRequestService';
import useToast from '../../../../../hooks/useToast';

const MonthlyPteAutorizar = ({ id, contractNumber, effectiveDate, serviceDetails }) => {
  const [ubicationsData, setUbicationsData] = useState(null);
  const [selectedUbicationData, setSelectedUbicationData] = useState(null);
  const [selectedPendingLines, setSelectedPendingLines] = useState([]);
  const [authorizationError, setAuthorizationError] = useState('');
  // To limit the opened accordions: Maximum 1 at a time.
  const [openedAccordion, setOpenedAccordion] = useState('');

  const { setLoader } = useContext(LoaderContext);
  const { setNewToast } = useToast();

  const toggleAccordion = (ubicationId) => {
    if (ubicationId === openedAccordion) {
      setOpenedAccordion('');

      return;
    }
    setOpenedAccordion(ubicationId);
  };

  // Fetch all ubications data for MSDS
  const fetchUbicationsData = useCallback(
    async () => {
      try {
        setLoader(true);
        // Fetch data
        const data = await postPendingAuthorizationMonthlyUbications(id);

        setUbicationsData(data);
      } catch (err) {
        setUbicationsData(null);
      } finally {
        setLoader(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, contractNumber, effectiveDate, setLoader]
  );

  // Fetch data for a specific ubication
  const fetchSelectedUbicationData = useCallback(async (minDate, maxDate, ubication) => {
    try {
      setLoader(true);
      // Fetch data
      const body = {
        idSolicitud: id,
        numeroContrato: contractNumber,
        idUbicacion: ubication.ubicationId,
        fechaDesde: minDate || null,
        fechaHasta: maxDate || null,
        ubicacionNombre: ubication.ubicationName
      };

      const data = await postPendingAuthorizationMonthly(body);

      // Fetch Calendar data
      const calendarBody = {
        idSolicitud: id,
        numeroContrato: contractNumber,
        idUbicacion: ubication.ubicationId,
        ubicacionNombre: ubication.ubicationName
      };

      const calendarData = await postCalendarData(calendarBody);

      if (data.pendingAmount) {
        setSelectedPendingLines(data.lines);
      }

      setSelectedUbicationData({
        ...data,
        ...calendarData
      });
    } catch (err) {
      setSelectedUbicationData(null);
    } finally {
      setLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Authorize ONLY the selectedLines corresponding to the ubicationId received
  const authorizeSelectedPendingLines = async (selectedLines) => {
    try {
      setLoader(true);
      const idsToAuthorize = selectedLines.reduce(
        (acc, line) => [...acc, ...line.idParteTrabajoDiariosCostos],
        []
      );

      const response = await postActionsInspector({
        action: 'Autorizar',
        parteDiarioCostoIds: idsToAuthorize
      });

      setNewToast({
        type: 'SUCCESS',
        message: response.message || 'Líneas autorizadas correctamente.'
      });

      toggleAccordion('');
      fetchUbicationsData();
    } catch (err) {
      setAuthorizationError(err?.data?.mensaje);
      setNewToast({
        type: 'ERROR',
        message: 'Ha ocurrido un error al autorizar las líneas seleccionadas.'
      });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchUbicationsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clean authorization error on open or close an accordion
    setAuthorizationError('');
  }, [openedAccordion]);

  // Fetch ubication data when open the accordion
  useEffect(() => {
    if (openedAccordion === '') {
      setSelectedUbicationData(null);
      setSelectedPendingLines([]);

      return;
    }
    const ubication = ubicationsData?.find((u) => u.ubicationId === openedAccordion);

    if (!ubication) return;
    fetchSelectedUbicationData(null, null, ubication);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedAccordion]);

  const getComponent = () => {
    if (ubicationsData?.length > 0) {
      return (
        <div className="d-flex flex-column gap-3 m-2">
          {ubicationsData.map((ubication) => (
            <div className="monthly-pte-autorizar-accordion" key={ubication.ubicationId}>
              <Accordion
                availableManualOpening
                leftContent={
                  <p className="d-flex gap-2 my-auto align-items-center">
                    <span className="accordion-ubication-label">Ubicación</span>
                    <BoxesWithText backgroundColor="#F7F7F7" color="#000000">
                      <span>{ubication.ubicationName}</span>
                    </BoxesWithText>
                    <BoxesWithText backgroundColor="#d6d6d6" color="#000000">
                      <span>{ubication.event}</span>
                    </BoxesWithText>
                    <BoxesWithText backgroundColor="#F7F7F7" color="#000000">
                      <span>{ubication.team}</span>
                    </BoxesWithText>
                  </p>
                }
                setShowManualContent={() => toggleAccordion(ubication.ubicationId)}
                showManualContent={ubication.ubicationId === openedAccordion}
                width="100%"
              >
                <div className="d-flex flex-column align-items-center">
                  <PteAutorizar
                    arrayDate={selectedUbicationData?.calendarDates}
                    authorizationError={authorizationError}
                    data={{
                      ...ubication,
                      mensaje: selectedUbicationData?.message,
                      isMSDS: true,
                      montoPendiente: selectedUbicationData?.pendingAmount,
                      lineas: selectedUbicationData?.lines,
                      contieneArchivosAdjuntos: selectedUbicationData?.contieneArchivosAdjuntos,
                      objetoImputacion: selectedUbicationData?.objetoImputacion,
                      objetoImputacionModificado: selectedUbicationData?.objetoImputacionModificado
                    }}
                    fetchData={fetchSelectedUbicationData}
                    maxDate={selectedUbicationData?.lastDate}
                    minDate={selectedUbicationData?.firstDate}
                    selectedLines={selectedPendingLines}
                    serviceDetails={serviceDetails}
                    setSelectedLines={setSelectedPendingLines}
                  />
                  {selectedUbicationData?.lines?.length > 0 && (
                    <Button
                      disabled={
                        !selectedUbicationData?.allowAuthorization ||
                        selectedPendingLines.length === 0
                      }
                      key="buttonAutorizar"
                      label="Autorizar"
                      onClick={() => authorizeSelectedPendingLines(selectedPendingLines)}
                      variant="outline-success"
                    />
                  )}
                </div>
              </Accordion>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="d-flex flex-column justify-content-center align-items-center p-3">
        <div>
          <MdAssignment size={50} />
        </div>
        <div className="pt-3">
          <p>
            <strong>
              {ubicationsData?.mensaje
                ? ubicationsData?.mensaje
                : 'No quedan posiciones y líneas pendientes de autorizar'}
            </strong>
          </p>
        </div>
      </div>
    );
  };

  return getComponent();
};

export default MonthlyPteAutorizar;

MonthlyPteAutorizar.propTypes = {
  id: PropTypes.number.isRequired,
  effectiveDate: PropTypes.string.isRequired,
  contractNumber: PropTypes.string.isRequired
};
